import React, { useCallback, useEffect, useRef, useState } from "react";
import { Gauge } from "../components/Gauge";
import { TrafficLight } from "../components/TrafficLight";
import { ActivityGrid } from "../components/ActivityGrid";
import { MobileAdvisor } from "../components/MobileAdvisor";
import { AdSlot } from "../components/AdSlot";
import { AD_SLOTS } from "../lib/adSlots";
import { runSpeedTest, computeVerdict, computeActivities } from "../lib/speedtest";
import { api, getSessionId } from "../lib/api";
import { trackClick } from "../lib/analytics";
import { Play, RefreshCw, Wrench, Activity } from "lucide-react";
import { Link } from "react-router-dom";

const STAGES = ["idle", "ping", "download", "upload", "done"];
const STAGE_LABELS = {
  idle: "Ready",
  ping: "Measuring latency",
  download: "Measuring download",
  upload: "Measuring upload",
  done: "Test complete",
};

export default function SpeedTestPage() {
  const [stage, setStage] = useState("idle");
  const [ping, setPing] = useState(0);
  const [jitter, setJitter] = useState(0);
  const [download, setDownload] = useState(0);
  const [upload, setUpload] = useState(0);
  const [verdict, setVerdict] = useState(null);
  const [history, setHistory] = useState([]);
  const [geo, setGeo] = useState(null);
  const sessionId = useRef(getSessionId());

  const loadHistory = useCallback(async () => {
    try {
      const r = await api.get("/speedtest/history", { params: { session_id: sessionId.current, limit: 5 } });
      setHistory(r.data || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    loadHistory();
    api.get("/geo").then((r) => setGeo(r.data)).catch(() => {});
  }, [loadHistory]);

  const start = async () => {
    if (stage !== "idle" && stage !== "done") return;
    trackClick("speed-test-start");
    setPing(0); setJitter(0); setDownload(0); setUpload(0); setVerdict(null);
    setStage("ping");

    try {
      const result = await runSpeedTest({
        onStage: (s) => setStage(s),
        onProgress: (p) => {
          if (p.stage === "download" && p.mbps != null) setDownload(Number(p.mbps.toFixed(2)));
          if (p.stage === "upload" && p.mbps != null) setUpload(Number(p.mbps.toFixed(2)));
        },
      });

      setPing(result.pingMs);
      setJitter(result.jitterMs);
      setDownload(result.downloadMbps);
      setUpload(result.uploadMbps);
      const v = computeVerdict(result);
      setVerdict(v);
      setStage("done");

      try {
        await api.post("/speedtest/save", {
          session_id: sessionId.current,
          ping_ms: result.pingMs,
          jitter_ms: result.jitterMs,
          download_mbps: result.downloadMbps,
          upload_mbps: result.uploadMbps,
          isp: geo?.isp,
          city: geo?.city,
          country: geo?.country,
          verdict: v.level,
        });
        loadHistory();
      } catch (_) {}
    } catch (e) {
      console.error("speed test failed", e);
      setStage("done");
      setVerdict({ level: "poor", label: "Test failed", message: "We couldn't complete the test. Please check your connection and try again." });
    }
  };

  const running = stage === "ping" || stage === "download" || stage === "upload";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-5 lg:px-8 py-8 sm:py-10 lg:py-16">
      <header className="mb-8 sm:mb-10">
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--cobalt-bright)] mb-2">/ Speed test</div>
        <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">How fast is your internet, really?</h1>
        <p className="mt-3 text-sm sm:text-base text-[var(--text-dim)] max-w-2xl">
          We test ping, download and upload against Cloudflare's global network — then translate the numbers into plain English.
        </p>
      </header>

      {/* Status bar */}
      <div className="glass rounded-xl px-4 sm:px-5 py-3 mb-6 sm:mb-8 flex flex-wrap items-center gap-3 sm:gap-4 justify-between" data-testid="speed-status-bar">
        <div className="flex items-center gap-3 text-sm">
          <div className="flex items-center gap-2">
            {running ? (
              <span className="dot dot-up pulse" />
            ) : stage === "done" ? (
              <span className="dot dot-up" />
            ) : (
              <span className="dot" style={{ background: "#3b4266" }} />
            )}
            <span className="font-mono uppercase tracking-wider text-xs text-[var(--text-dim)]">
              {STAGE_LABELS[stage] || "Ready"}
            </span>
          </div>
          {geo?.isp && (
            <div className="hidden sm:block text-xs text-[var(--text-muted)]">
              via <span className="text-white">{geo.isp}</span> · {geo.city}, {geo.country}
            </div>
          )}
        </div>
        <button
          onClick={start}
          disabled={running}
          data-testid="speed-test-start-btn"
          className="btn-cobalt inline-flex items-center gap-2 px-5 py-2 rounded-md text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {stage === "idle" && (<><Play className="w-4 h-4" /> Start test</>)}
          {running && (<><RefreshCw className="w-4 h-4 animate-spin" /> Running…</>)}
          {stage === "done" && (<><RefreshCw className="w-4 h-4" /> Run again</>)}
        </button>
      </div>

      {/* Gauges */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 mb-6 sm:mb-8">
        <div className="glass rounded-2xl p-5 sm:p-6 flex flex-col items-center">
          <Gauge value={download} max={500} label="Download" unit="Mbps" active={stage === "download"} testid="gauge-download" />
        </div>
        <div className="glass rounded-2xl p-5 sm:p-6 flex flex-col items-center">
          <Gauge value={upload} max={300} label="Upload" unit="Mbps" active={stage === "upload"} testid="gauge-upload" />
        </div>
        <div className="glass rounded-2xl p-5 sm:p-6 flex flex-col justify-between">
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-[var(--text-muted)] mb-4">Latency</div>
            <div className="space-y-4">
              <Metric label="Ping" value={ping} unit="ms" active={stage === "ping"} testid="metric-ping" />
              <Metric label="Jitter" value={jitter} unit="ms" active={stage === "ping"} testid="metric-jitter" />
            </div>
          </div>
          <div className="mt-6 text-xs text-[var(--text-muted)] font-mono">
            Lower is better. Good ping &lt; 60 ms.
          </div>
        </div>
      </div>

      {/* Verdict + Activities */}
      {verdict && (
        <div className="mb-6 sm:mb-8 space-y-4 sm:space-y-6">
          <TrafficLight verdict={verdict} testid="speed-verdict" />
          <ActivityGrid activities={computeActivities({ pingMs: ping, jitterMs: jitter, downloadMbps: download, uploadMbps: upload })} />
          <AdSlot slot={AD_SLOTS.speedResult} name="speed-result" minHeight={120} />
          <MobileAdvisor testid="speed-mobile-advisor" />
          {verdict.level !== "good" && (
            <Link to="/fix-my-internet" className="inline-flex items-center gap-2 text-sm text-[var(--cobalt-bright)] hover:text-white transition" data-testid="verdict-fix-link">
              <Wrench className="w-4 h-4" /> Open the Fix-My-Internet wizard
            </Link>
          )}
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="glass rounded-2xl p-6" data-testid="speed-history-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <Activity className="w-4 h-4 text-[var(--cobalt-bright)]" /> Recent runs
            </h3>
            <span className="text-xs text-[var(--text-muted)] font-mono">last 5 on this device</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[var(--text-muted)] font-mono">
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">Down</th>
                  <th className="py-2 pr-4">Up</th>
                  <th className="py-2 pr-4">Ping</th>
                  <th className="py-2 pr-4">Verdict</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} className="border-t border-white/5">
                    <td className="py-2 pr-4 text-[var(--text-dim)] font-mono text-xs">{new Date(h.timestamp).toLocaleString()}</td>
                    <td className="py-2 pr-4 tabular-nums">{Number(h.download_mbps).toFixed(1)} Mbps</td>
                    <td className="py-2 pr-4 tabular-nums">{Number(h.upload_mbps).toFixed(1)} Mbps</td>
                    <td className="py-2 pr-4 tabular-nums">{Math.round(h.ping_ms)} ms</td>
                    <td className="py-2 pr-4">
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${
                        h.verdict === "good" ? "bg-emerald-500/10 text-emerald-300" :
                        h.verdict === "fair" ? "bg-amber-500/10 text-amber-300" :
                        "bg-rose-500/10 text-rose-300"
                      }`}>
                        <span className={`dot dot-${h.verdict === "good" ? "up" : h.verdict === "fair" ? "slow" : "down"}`} />
                        {h.verdict}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function Metric({ label, value, unit, active, testid }) {
  return (
    <div className="flex items-end justify-between">
      <span className="text-sm text-[var(--text-dim)]">{label}</span>
      <span className={`font-display text-2xl tabular-nums ${active ? "text-[var(--cobalt-bright)]" : "text-white"}`} data-testid={testid}>
        {value ? Math.round(value) : 0}
        <span className="text-sm text-[var(--text-muted)] ml-1 font-mono">{unit}</span>
      </span>
    </div>
  );
}
